import { defineStore, acceptHMRUpdate } from 'pinia'
import { DateTime } from 'luxon'
import { useConfigStore } from './ConfigStore'
import { SurveyService } from '@/api/client'
import { report } from '@/classes/manager/ExceptionReportingManager'
import { AppReview } from '@capawesome/capacitor-app-review'
import { usePlatformInfo } from '@/composables/usePlatformInfo'
import { useAuthStore } from './AuthStore'

const { isWeb } = usePlatformInfo()

type TSurveyAnswer = {
  survey_id: number
  recorded_at: string
  device_id: number | null
  app_version: string
  device_type: string
  device_os: string
  rating: number | null
  comment: string | null
  data: string
}

type TSurvey = {
  id: number
  starts_at: string | null
  ends_at: string | null
}

type TState = {
  ongoingSurvey: TSurvey | null
  currentAnswer: TSurveyAnswer | null
  answeredSurveyIds: number[]
  dismissedSurveys: { [key: number]: string }
  lastScore: number | null
  lastSurveyAnsweredAt: string | null
  lastPromptedForAppStoreReviewAt: string | null
  selectedDetailledFeedbackOptions: TDetailledFeedbackOptions[]
  stars: { [key in TStarableFeedbackOptions]: number | null }
}

type TDetailledFeedbackOptions =
  | 'hard_to_use'
  | 'lacking_features'
  | 'does_not_help_me'
  | 'confusing'
  | 'poorly_implemented'
  | 'too_buggy'
  | 'too_slow'

type TStarableFeedbackOptions =
  | 'how_happy_are_you_with_the_introduction_of_clockin'
  | 'how_much_do_you_enjoy_using_clockin'
  | 'how_much_easier_does_clockin_make_your_work'
  | 'clarity_of_the_app'
  | 'simplicity_of_use'
  | 'app_features'

const initialState: TState = {
  ongoingSurvey: null,
  currentAnswer: null,
  answeredSurveyIds: [],
  dismissedSurveys: {},
  lastScore: null,
  lastSurveyAnsweredAt: null,
  lastPromptedForAppStoreReviewAt: null,
  selectedDetailledFeedbackOptions: [],
  stars: {
    how_happy_are_you_with_the_introduction_of_clockin: null,
    how_much_do_you_enjoy_using_clockin: null,
    how_much_easier_does_clockin_make_your_work: null,
    clarity_of_the_app: null,
    simplicity_of_use: null,
    app_features: null,
  },
}

const useSurveyStore = defineStore('surveyStore', {
  state: () => {
    return { ...initialState }
  },
  getters: {
    hasActiveSurvey(state): boolean {
      if (!state.ongoingSurvey) return false
      if (
        state.dismissedSurveys[state.ongoingSurvey.id] &&
        DateTime.fromISO(
          state.dismissedSurveys[state.ongoingSurvey.id],
        ).diffNow('days').days > -3
      )
        return false
      if (state.answeredSurveyIds.includes(state.ongoingSurvey.id)) return false
      const surveyStartsInFuture =
        state.ongoingSurvey.starts_at &&
        DateTime.fromISO(state.ongoingSurvey.starts_at) > DateTime.now()
      const surveyEndsInPast =
        state.ongoingSurvey.ends_at &&
        DateTime.fromISO(state.ongoingSurvey.ends_at) < DateTime.now()
      if (surveyStartsInFuture || surveyEndsInPast) return false
      return true
    },
    detailledFeedbackOptionIsSelected:
      (state) =>
      (option: TDetailledFeedbackOptions): boolean => {
        return state.selectedDetailledFeedbackOptions.includes(option)
      },
    itIsAppropriateToAskForReview(): boolean {
      if (!useAuthStore().isPersonalDevice) {
        return false
      }
      if (this.lastScore && this.lastScore < 8) {
        return false
      }
      if (
        useConfigStore().build_date > DateTime.now().minus({ days: 14 }).toISO()
      ) {
        return false
      }

      if (
        this.lastPromptedForAppStoreReviewAt &&
        Math.abs(
          DateTime.fromISO(this.lastPromptedForAppStoreReviewAt).diffNow('days')
            .days,
        ) < 90
      ) {
        return false
      }
      if (isWeb) {
        console.log('not asking for review because we are in web context')
        return false
      }

      return true
    },
  },

  actions: {
    startSurvey() {
      if (!this.ongoingSurvey) return
      this.currentAnswer = {
        survey_id: this.ongoingSurvey.id,
        recorded_at: DateTime.now().toISO(),
        device_id: useConfigStore().device_id,
        app_version: useConfigStore().appVersion,
        device_type: useConfigStore().device_type,
        device_os: useConfigStore().device_os ?? '',
        rating: null,
        comment: '',
        data: '',
      }
    },

    dismissSurvey() {
      this.dismissedSurveys[this.ongoingSurvey?.id ?? 0] =
        DateTime.now().toISO()
      this.currentAnswer = null
      this.ongoingSurvey = null
    },

    setRating(rating: number) {
      if (!this.currentAnswer)
        throw new Error('Cannot set rating, No survey started!')
      this.currentAnswer.rating = rating
      this.lastScore = rating
      this.lastSurveyAnsweredAt = DateTime.now().toISO()
    },

    handleDetailledFeedbackOptionClicked(option: TDetailledFeedbackOptions) {
      if (!this.currentAnswer)
        throw new Error('Cannot select option, No survey started!')

      if (this.selectedDetailledFeedbackOptions.includes(option)) {
        this.selectedDetailledFeedbackOptions =
          this.selectedDetailledFeedbackOptions.filter((o) => o !== option)
      } else {
        if (this.selectedDetailledFeedbackOptions.length < 3) {
          this.selectedDetailledFeedbackOptions.push(option)
        }
      }
    },

    handleStarsSelected(option: TStarableFeedbackOptions, stars: number) {
      if (!this.currentAnswer)
        throw new Error('Cannot set stars, No survey started!')
      if (stars < 0 || stars > 5) return
      this.stars[option] = stars
    },

    setComment(comment: string) {
      if (!this.currentAnswer)
        throw new Error('Cannot set comment, No survey started!')
      this.currentAnswer.comment = comment
    },

    endSurvey() {
      if (!this.currentAnswer) return
      this.currentAnswer.data = JSON.stringify({
        selectedDetailledFeedbackOptions: this.selectedDetailledFeedbackOptions,
        stars: this.stars,
      })
      this.selectedDetailledFeedbackOptions = []
      this.stars = {
        how_happy_are_you_with_the_introduction_of_clockin: null,
        how_much_do_you_enjoy_using_clockin: null,
        how_much_easier_does_clockin_make_your_work: null,
        clarity_of_the_app: null,
        simplicity_of_use: null,
        app_features: null,
      }
      this.dismissedSurveys = {}
      this.answeredSurveyIds.push(this.ongoingSurvey?.id ?? 0)
      void this.push()
      this.currentAnswer = null
    },

    sync() {
      console.log('syncing SurveyStore')
      void this.push()
      void this.pull()
    },

    async promptForAppStoreReviewIfAppropriate() {
      if (!this.itIsAppropriateToAskForReview) return
      console.log('-------- PROMPTING FOR APP STORE REVIEW --------')
      this.lastPromptedForAppStoreReviewAt = DateTime.now().toISO()
      try {
        await AppReview.requestReview()
      } catch (_) {
        // If in-app review fails, fall back to app store
        await AppReview.openAppStore()
      }
    },

    async push() {
      console.log('pushing SurveyStore', this.currentAnswer)
      if (this.currentAnswer) {
        try {
          await SurveyService.postSurveyAnswer(this.currentAnswer)
        } catch (e) {
          report(e as Error)
        }
      }
    },

    async pull() {
      console.log('pulling SurveyStore')
      try {
        const surveyResponse = await SurveyService.getSurvey()
        if (surveyResponse.data && surveyResponse.data?.length > 0) {
          this.ongoingSurvey = surveyResponse.data[0]
        } else {
          this.ongoingSurvey = null
        }
      } catch (e) {
        console.error(e)
      }
    },

    hydrate(state: string) {
      this.$state = JSON.parse(state)
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSurveyStore, import.meta.hot))
}

export { useSurveyStore }
